import messages from "messages";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormControl, FormGroup, FormLabel } from "react-bootstrap";
// components
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus.svg";
import HideFromPublicView from "components/hide-from-public-view/HideFromPublicView";
import ModalLayout, {
  ModalLayoutProps,
} from "components/modal/modal-layout/ModalLayout";
import RequiredAsterisk from "components/required-asterisk";
import SessionTypeLengthEditor from "../../../../components/layout/components/calendar-view/components/session-type-length-editor/SessionTypeLengthEditor";
import SessionTypeNameEditor from "../../../../components/layout/components/calendar-view/components/session-type-name-editor/SessionTypeNameEditor";
import SessionTypePriceEditor from "../../../../components/layout/components/calendar-view/components/session-type-price-editor/SessionTypePriceEditor";
// styles
import styles from "./SessionTypeEditor.module.scss";
// utils
import ConfirmModal from "components/confirm-modal";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import useToggleModal from "hooks/useToggleModal";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import isValidSessionLength from "../../../../components/layout/components/calendar-view/utils/isValidSessionLength";
import isValidSessionType from "../../../../components/layout/components/calendar-view/utils/isValidSessionType";
import SessionTypeColorSelector from "../session-type-color-selector/SessionTypeColorSelector";

const AMPLITUDE_COMPONENT_NAME = "session-type-editor";

const MAX_LENGTH = 500;
type Props = ModalLayoutProps & {
  defaultValue: Partial<SessionType>;
  onApply: (arg: SessionType) => void;
  onDelete?: (arg: SessionType) => void;
  getIsLabelInvalid: (arg: SessionType["label"] | undefined) => boolean;
  amplitudePageName: AmplitudePageName;
};

const SessionTypeEditor = ({
  onApply,
  getIsLabelInvalid,
  show,
  onHide,
  onDelete,
  defaultValue,
  amplitudePageName,
  ...props
}: Props) => {
  const { isPhonePortrait } = usePhonePortraitContext();

  const [value, setValue] = useState(defaultValue);
  const [isDirty, setIsDirty] = useState(false);

  const mode = useMemo<"ADD" | "EDIT">(
    () => (defaultValue.label ? "EDIT" : "ADD"),
    [defaultValue.label],
  );

  useEffect(() => {
    setIsDirty(false);

    if (show) {
      setValue(defaultValue);
    } else {
      setValue({});
    }
  }, [show, defaultValue]);

  const {
    show: showConfirmModal,
    open: openConfirmModal,
    close: closeConfirmModal,
  } = useToggleModal(false);

  const { headerLabelTitle, footerLabelConfirm } = useMemo(
    () =>
      mode === "ADD"
        ? {
            headerLabelTitle: (
              <>
                <OutlinePlus16 /> {messages.addANewSessionType}
              </>
            ),
            footerLabelConfirm: messages.create,
          }
        : {
            headerLabelTitle: messages.editSessionType,
            footerLabelConfirm: messages.save,
          },
    [mode],
  );

  const saveChanges = useCallback(() => {
    if (onHide) {
      onHide();
    }
    if (value) {
      onApply(value as SessionType);
    }
  }, [onApply, onHide, value]);

  return (
    <>
      <ModalLayout
        {...props}
        show={show}
        onHide={onHide}
        Header={headerLabelTitle}
        showHeaderDivider={true}
        Footer={
          <div
            className={getClasses(
              styles["footer-content"],
              isPhonePortrait && styles["footer-content--phone"],
            )}
          >
            {onDelete ? (
              <Button
                variant="outline-primary"
                className={styles["footer-btn"]}
                onClick={(e) => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "cancel-button"],
                    "click",
                  );
                  onDelete(value as SessionType);
                  onHide && onHide(e);
                }}
              >
                Delete Session Type
              </Button>
            ) : (
              <Button
                variant="outline-primary"
                className={styles["footer-btn"]}
                onClick={(e) => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "cancel-button"],
                    "click",
                  );
                  onHide && onHide(e);
                }}
              >
                {messages.cancel}
              </Button>
            )}
            <Button
              variant="primary"
              className={styles["footer-btn"]}
              disabled={!isValidSessionType(value)}
              onClick={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "confirm-button"],
                  "click",
                );

                if (mode === "EDIT" && isDirty) {
                  openConfirmModal();
                  return;
                }

                saveChanges();
              }}
            >
              {footerLabelConfirm}
            </Button>
          </div>
        }
        dialogClassName={styles["dialog"]}
        bodyClass={getClasses(
          styles["body"],
          isPhonePortrait && styles["body--phone"],
        )}
      >
        <div className={getClasses(styles["section"])}>
          <FormGroup
            controlId="sessionTypeLabel"
            className={getClasses(styles["form-group"])}
          >
            <FormLabel className={styles["label"]}>
              {messages.titleOfTheSessionType}
              <RequiredAsterisk />
            </FormLabel>
            {show && (
              <SessionTypeNameEditor
                value={value.label}
                onChange={(e) => {
                  setIsDirty(true);
                  setValue((prev) => ({ ...prev, label: e.target.value }));
                }}
                isInvalid={
                  defaultValue.label === value.label
                    ? false
                    : getIsLabelInvalid(value.label)
                }
                onFocus={() => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "session-type-title-field"],
                    "focus",
                  );
                }}
              />
            )}
          </FormGroup>
          <FormGroup
            className={getClasses(
              styles["form-group"],
              styles["form-group--invite-only"],
            )}
            controlId="sessionTypeInviteOnly"
          >
            <FormLabel
              className={getClasses(
                styles["label"],
                styles["label--invite-only"],
              )}
            >
              {messages.setSessionToPrivate}
            </FormLabel>
            <HideFromPublicView
              className={styles["invite-only-check"]}
              checked={!!value.invite_only}
              onChange={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "hide-from-public-view-checkbox"],
                  "click",
                );
                setIsDirty(true);
                setValue((prev) => ({
                  ...prev,
                  invite_only: !value.invite_only,
                }));
              }}
            />
          </FormGroup>
        </div>
        <div className={getClasses(styles["section"], styles["section--row"])}>
          <FormGroup
            className={styles["form-group"]}
            controlId="sessionTypeLength"
          >
            <FormLabel className={styles["label"]}>
              {messages.length}
              <RequiredAsterisk />
            </FormLabel>
            {show && (
              <SessionTypeLengthEditor
                isInvalid={!isValidSessionLength(value.length_min)}
                value={value.length_min}
                onChange={(e) => {
                  setIsDirty(true);
                  setValue((prev) => ({
                    ...prev,
                    length_min:
                      e.target.value === "" ? undefined : +e.target.value,
                  }));
                }}
                onFocus={() => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "session-length-field"],
                    "focus",
                  );
                }}
              />
            )}
          </FormGroup>
          <FormGroup
            className={styles["form-group"]}
            controlId="sessionTypePrice"
          >
            <FormLabel className={styles["label"]}>
              {messages.price}
              <RequiredAsterisk />
            </FormLabel>
            {show && (
              <SessionTypePriceEditor
                value={value.price}
                onChangeValue={(price) => {
                  setIsDirty(true);
                  setValue((prev) => ({
                    ...prev,
                    price,
                  }));
                }}
                onFocus={() => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "price-field"],
                    "focus",
                  );
                }}
                onCheck={() => {
                  track(
                    amplitudePageName,
                    [AMPLITUDE_COMPONENT_NAME, "free-checkbox"],
                    "click",
                  );
                }}
              />
            )}
          </FormGroup>
        </div>
        <FormGroup
          className={getClasses(styles["form-group"])}
          controlId="sessionTypeDescription"
        >
          <FormLabel
            className={getClasses(
              styles["label"],
              styles["label--description"],
            )}
          >
            {messages.addDescriptionOfThisSession}
            <span>
              {value.description?.length ?? 0}/{MAX_LENGTH}
            </span>
          </FormLabel>
          {show && (
            <FormControl
              value={value.description || ""}
              as="textarea"
              className={styles["description"]}
              placeholder={messages.enterDescription}
              rows={3}
              maxLength={MAX_LENGTH}
              onChange={(e) => {
                setIsDirty(true);
                setValue((prev) => ({ ...prev, description: e.target.value }));
              }}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "session-description-field"],
                  "focus",
                );
              }}
            />
          )}
        </FormGroup>
        <SessionTypeColorSelector
          selectedColorLabel={value.color_code}
          onChange={(colorId) => {
            setIsDirty(true);
            setValue((prev) => ({ ...prev, color_code: colorId }));
          }}
        />
      </ModalLayout>
      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={messages.editSessionConfirmModalBody}
        labelConfirm={messages.continue}
        show={showConfirmModal}
        onCancel={() => {
          track(
            amplitudePageName,
            [AMPLITUDE_COMPONENT_NAME, "confirm-modal-cancel"],
            "click",
          );
          closeConfirmModal();
        }}
        onConfirm={() => {
          track(
            amplitudePageName,
            [AMPLITUDE_COMPONENT_NAME, "confirm-modal-confirm"],
            "click",
          );

          saveChanges();
          closeConfirmModal();
        }}
      />
    </>
  );
};
export default SessionTypeEditor;
